import { useEffect, useMemo, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { Add, Delete, FilterAlt, RemoveCircleOutline } from '@mui/icons-material'
import ReactECharts from 'echarts-for-react'

import {
    useCreateFunnelMutation,
    useDeleteFunnelMutation,
    useGetFunnelReportQuery,
    useListFunnelsQuery,
    usePreviewFunnelMutation,
} from '@/apis/v2Api'
import { cleanMatcher, matcherLabel, v2ErrorMessage, type FunnelReport } from '@/apis/v2Mappers'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { EmptyState } from '@/components/shared/EmptyState'
import { useReportArgs } from '@/hooks/useReportArgs'
import type { Period } from '@/utils/dates'

import {
    EstimateChip,
    ExactChip,
    ReportHeader,
    ReportStatus,
    SectionCard,
    fmtNum,
    fmtPct,
} from './common'
import { EMPTY_MATCHER, EVENT_NAME_RE, MatcherFields, type MatcherInput } from './MatcherFields'

function FunnelResult({ report }: { report: FunnelReport }) {
    const option = {
        tooltip: { trigger: 'axis' as const, axisPointer: { type: 'shadow' as const } },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: {
            type: 'category' as const,
            data: report.steps.map(s => `${s.step}. ${s.match.event}`),
        },
        yAxis: { type: 'value' as const },
        series: [
            {
                type: 'bar' as const,
                data: report.steps.map(s => s.count),
                itemStyle: { color: '#6366f1', borderRadius: [4, 4, 0, 0] },
                label: { show: true, position: 'top' as const },
            },
        ],
    }
    return (
        <Box>
            <ReactECharts option={option} style={{ height: 260 }} notMerge />
            <Box sx={{ mt: 2 }}>
                {report.steps.map(s => (
                    <Box
                        key={s.step}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 90px 90px 90px',
                            gap: 1,
                            py: 0.75,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Typography variant="body2" noWrap>
                            {s.step}. {matcherLabel(s.match)}
                        </Typography>
                        <Typography variant="body2" align="right">
                            {fmtNum(s.count)}
                        </Typography>
                        <Typography variant="body2" align="right" color="text.secondary">
                            {s.step === 1 ? '' : fmtPct(s.rate_from_previous)}
                        </Typography>
                        <Typography variant="body2" align="right" fontWeight={600}>
                            {fmtPct(s.rate_from_start)}
                        </Typography>
                    </Box>
                ))}
                <Typography variant="caption" color="text.secondary">
                    Counted by {report.by}, steps in order within{' '}
                    {Math.round(report.window_ms / 86_400_000)} day(s) of step 1. Columns: count,
                    from previous step, from start.
                </Typography>
            </Box>
        </Box>
    )
}

/** Saved funnels (v2 `/funnels`) + results (`/reports/funnel`) + builder with live preview. */
export function FunnelsPage() {
    const [period, setPeriod] = useState<Period>('30d')
    const args = useReportArgs(period)
    const funnels = useListFunnelsQuery(args.projectId)
    const [selected, setSelected] = useState<string | null>(null)
    const list = useMemo(() => funnels.data ?? [], [funnels.data])
    useEffect(() => {
        if (list.length > 0 && !list.some(f => f.id === selected)) {
            setSelected(list[0].id)
        }
    }, [list, selected])

    const report = useGetFunnelReportQuery(
        { ...args, id: selected ?? '' },
        { skip: !selected || !list.some(f => f.id === selected) },
    )
    const [deleteFunnel] = useDeleteFunnelMutation()
    const [toDelete, setToDelete] = useState<{ id: string; name: string } | null>(null)

    // builder
    const [open, setOpen] = useState(false)
    const [name, setName] = useState('')
    const [steps, setSteps] = useState<MatcherInput[]>([EMPTY_MATCHER, EMPTY_MATCHER])
    const [windowDays, setWindowDays] = useState(7)
    const [by, setBy] = useState<'visitor' | 'session'>('visitor')
    const [preview, previewState] = usePreviewFunnelMutation()
    const [createFunnel, createState] = useCreateFunnelMutation()
    const stepsValid =
        steps.length >= 2 &&
        steps.length <= 10 &&
        steps.every(s => EVENT_NAME_RE.test(s.event.trim()))
    const def = () => ({ steps: steps.map(cleanMatcher), window_days: windowDays, by })

    const resetBuilder = () => {
        setName('')
        setSteps([EMPTY_MATCHER, EMPTY_MATCHER])
        setWindowDays(7)
        setBy('visitor')
        previewState.reset()
        createState.reset()
    }

    const save = async () => {
        try {
            const f = await createFunnel({
                projectId: args.projectId,
                name: name.trim(),
                ...def(),
            }).unwrap()
            setSelected(f.id)
            setOpen(false)
            resetBuilder()
        } catch {
            // shown from createState.error
        }
    }

    const current = list.find(f => f.id === selected)

    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader
                title="Funnels"
                subtitle="Ordered steps, each within the window of the first one."
                period={period}
                onPeriodChange={setPeriod}
                actions={
                    <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>
                        New funnel
                    </Button>
                }
            />
            <ReportStatus isLoading={funnels.isLoading} error={funnels.error}>
                {list.length === 0 ? (
                    <EmptyState
                        icon={<FilterAlt />}
                        title="No funnels yet"
                        description="Build a funnel from 2–10 steps (e.g. view_item → add_to_cart → purchase) and see where people drop off."
                        actionLabel="Build a funnel"
                        onAction={() => setOpen(true)}
                    />
                ) : (
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <SectionCard title="Saved funnels">
                                <List dense disablePadding>
                                    {list.map(f => (
                                        <ListItemButton
                                            key={f.id}
                                            selected={f.id === selected}
                                            onClick={() => setSelected(f.id)}
                                            sx={{ borderRadius: 1 }}
                                        >
                                            <ListItemText
                                                primary={f.name}
                                                secondary={`${f.steps.length} steps · ${f.window_days}d · by ${f.by}`}
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </SectionCard>
                        </Grid>
                        <Grid size={{ xs: 12, md: 9 }}>
                            <SectionCard
                                title={current?.name ?? 'Funnel'}
                                action={
                                    current && (
                                        <Tooltip title="Delete funnel">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    setToDelete({
                                                        id: current.id,
                                                        name: current.name,
                                                    })
                                                }
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )
                                }
                            >
                                <ReportStatus
                                    isLoading={report.isLoading || report.isFetching}
                                    error={report.error}
                                >
                                    {report.data && (
                                        <>
                                            <Box sx={{ mb: 1 }}>
                                                {report.data.exact ? (
                                                    <ExactChip />
                                                ) : (
                                                    <EstimateChip title="Estimate: the funnel is computed from sampled events. It is exact (unsampled) when every step is an exact conversion event." />
                                                )}
                                            </Box>
                                            <FunnelResult report={report.data} />
                                        </>
                                    )}
                                </ReportStatus>
                            </SectionCard>
                        </Grid>
                    </Grid>
                )}
            </ReportStatus>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>New funnel</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            label="Name"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            required
                            sx={{ flex: 2, minWidth: 200 }}
                        />
                        <TextField
                            size="small"
                            type="number"
                            label="Window (days)"
                            value={windowDays}
                            onChange={e =>
                                setWindowDays(
                                    Math.min(90, Math.max(1, Number(e.target.value) || 1)),
                                )
                            }
                            sx={{ width: 140 }}
                        />
                        <TextField
                            size="small"
                            select
                            label="Count by"
                            value={by}
                            onChange={e => setBy(e.target.value as 'visitor' | 'session')}
                            sx={{ width: 140 }}
                        >
                            <MenuItem value="visitor">Visitor</MenuItem>
                            <MenuItem value="session">Session</MenuItem>
                        </TextField>
                    </Box>
                    {steps.map((s, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <Typography sx={{ width: 24, pt: 1 }} fontWeight={600}>
                                {i + 1}.
                            </Typography>
                            <MatcherFields
                                dense
                                value={s}
                                onChange={v => setSteps(steps.map((x, j) => (j === i ? v : x)))}
                            />
                            <IconButton
                                size="small"
                                disabled={steps.length <= 2}
                                onClick={() => setSteps(steps.filter((_, j) => j !== i))}
                                sx={{ mt: 0.5 }}
                            >
                                <RemoveCircleOutline fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                    <Box>
                        <Button
                            size="small"
                            startIcon={<Add />}
                            disabled={steps.length >= 10}
                            onClick={() => setSteps([...steps, EMPTY_MATCHER])}
                        >
                            Add step
                        </Button>
                    </Box>
                    {(previewState.error || createState.error) && (
                        <Alert severity="error">
                            {v2ErrorMessage(createState.error ?? previewState.error)}
                        </Alert>
                    )}
                    {previewState.data && <FunnelResult report={previewState.data} />}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setOpen(false)
                            resetBuilder()
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={!stepsValid || previewState.isLoading}
                        onClick={() => {
                            void preview({ ...args, ...def() })
                        }}
                    >
                        Preview
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!stepsValid || !name.trim() || createState.isLoading}
                        onClick={() => {
                            void save()
                        }}
                    >
                        Save funnel
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={!!toDelete}
                title="Delete funnel?"
                message={`"${toDelete?.name ?? ''}" will be removed. Event data is not affected.`}
                confirmLabel="Delete"
                confirmColor="error"
                onConfirm={() => {
                    if (toDelete) {
                        void deleteFunnel({ projectId: args.projectId, id: toDelete.id })
                    }
                    setToDelete(null)
                }}
                onCancel={() => setToDelete(null)}
            />
        </Box>
    )
}
